import React, { useState, useEffect } from "react";
import { Form, Input, InputNumber, Button, Select, Modal, DatePicker, Row, Col } from "antd";
import axios from "axios";
import SelectComp from "../../Select/Select";
import moment from "moment";
import MainButtonComp from "../../Buttons/MainButtonComp";
import { Error, Success } from "../../Messages/Messages";
import { generalErrorAlert } from "../../GeneralErrorAlert";
import InventoryUsage from "../../Inventory/InventoryUsage";
import { useTranslation } from "react-i18next";

const { TextArea } = Input;

export const UpdateMilestone = ({
  iconStyle = {},
  milestone,
  employeesArray,
  updateParent,
  inventoryList
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation("dashboard");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [team, setTeam] = useState([]);

  const showModal = () => {
    setIsModalOpen(true);
    let teamArray = milestone?.team?.map(t => t?._id);
    setTeam(teamArray);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleChange = val => {
    setTeam(val);
  };

  const onFinish = async values => {
    if (team.length === 0) return Error(t("Please add a team"));
    setLoading(true);
    const updatedValues = { ...values, team };
    await axios
      .put(
        `${process.env.REACT_APP_BACKEND_URL}/milestones/update/${milestone._id}`,
        updatedValues,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token")
          }
        }
      )
      .then(res => {
        setLoading(false);
        if (res.status === 200 || res.status === 304) {
          Success(res.data.successMessage);
          handleCancel();
          form.resetFields();
          updateParent(res.data?.milestone);
        } else {
          Error(res.data.errorMessage);
        }
      })
      .catch(err => {
        setLoading(false);
        generalErrorAlert(err);
      });
  };

  useEffect(() => {
    if (isModalOpen) {
      const processedInventory =
        milestone?.inventory?.map(item => ({
          item: item?.item?._id || item?.item,
          total: item?.total,
          minimumAmount: item?.minimumAmount
        })) || [];

      form.setFieldsValue({
        name: milestone.name,
        description: milestone.description,
        paymentType: milestone.paymentType,
        price: milestone.price,
        currency: milestone.currency,
        startDate: milestone.startDate ? moment(milestone.startDate) : null,
        endDate: milestone.endDate ? moment(milestone.endDate) : null,
        inventory: processedInventory
      });

      let teamArray = milestone?.team?.map(t => t?._id);
      setTeam(teamArray);
    }
  }, [isModalOpen, milestone]);

  return (
    <>
      <MainButtonComp text="Edit" editBtn onClick={showModal} />
      <Modal
        title={t("Update Milestone")}
        open={isModalOpen}
        onCancel={handleCancel}
        width={720}
        destroyOnClose={true}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            {t("Cancel")}
          </Button>,
          <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()}>
            {t("Update Milestone")}
          </Button>
        ]}
      >
        <Form form={form} onFinish={onFinish} layout="vertical" preserve={false}>
          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                label={t("Name")}
                name="name"
                rules={[{ required: true, message: t("enter", { field: t("Name") }) }]}
              >
                <Input size="large" placeholder={t("e.g. Phase 1 - Foundation")} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={t("Payment Type")} name="paymentType">
                <Select size="large" placeholder={t("Select payment type")}>
                  <Select.Option value="hourly">{t("Hourly")}</Select.Option>
                  <Select.Option value="totalAmount">{t("Total Amount")}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={8}>
              <Form.Item label={t("Amount")} name="price">
                <InputNumber min={1} style={{ width: "100%" }} size="large" placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={t("Currency")} name="currency">
                <Select size="large" placeholder={t("Select currency")}>
                  <Select.Option value="$">{t("USD")}</Select.Option>
                  <Select.Option value="€">{t("Euro")}</Select.Option>
                  <Select.Option value="Custom">{t("Custom")}</Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label={t("Start Date")} name="startDate">
                <DatePicker
                  style={{ width: "100%" }}
                  format={"DD/MMM/YYYY"}
                  size="large"
                  placeholder={t("Select start date")}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <Form.Item label={t("Description")} name="description">
                <TextArea rows={4} placeholder={t("Enter milestone details...")} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <Form.Item label={t("Team")} required>
                <div>
                  <SelectComp
                    type="tags"
                    handleChange={handleChange}
                    options={employeesArray}
                    value={team?.length > 0 ? team : []}
                    placeholder={t("Select team members")}
                  />
                </div>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24}>
              <div>
                <h3 className="mb-3">{t("Inventory")}</h3>
                <InventoryUsage
                  currentInventoryData={milestone?.inventory}
                  form={form}
                  inventoryList={inventoryList}
                />
              </div>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};
